import { PageHero } from "@/components/common";
import { Notice } from "@/components/Notices";
import { Layers, ShieldCheck, Bot, ScrollText, CheckCircle2 } from "lucide-react";

const LEVELS = [
  {
    n: 1,
    title: "Consulta abierta",
    desc: "Participación voluntaria de usuarios elegibles. Refleja a quienes decidieron participar; no es una muestra representativa.",
  },
  {
    n: 2,
    title: "Participación verificada",
    desc: "Participantes con correo o teléfono confirmado. Reduce duplicados, pero sigue siendo participación voluntaria.",
  },
  {
    n: 3,
    title: "Consulta dirigida",
    desc: "Participación de un grupo previamente definido para un tema o población específica.",
  },
  {
    n: 4,
    title: "Estudio metodológico",
    desc: "Muestra diseñada, ponderada y revisada por una persona cualificada. Solo este nivel puede aproximarse a la representatividad.",
  },
];

const DISTINCTIONS = [
  ["Opinión", "Lo que la persona piensa o prefiere."],
  ["Percepción", "Cómo la persona interpreta una situación."],
  ["Experiencia directa", "Algo que la persona vivió de primera mano."],
  ["Conocimiento declarado", "Nivel de información que la persona dice tener."],
  ["Información documental", "Datos provenientes de documentos o fuentes públicas."],
];

export default function Metodologia() {
  return (
    <div data-testid="metodologia-page">
      <PageHero
        label="Metodología"
        title="Cómo hacemos las consultas"
        subtitle="Cada consulta de 2Voto indica su método, periodo, número de participantes y limitaciones. Creemos en la transparencia metodológica: entender cómo se produjo un resultado es tan importante como el resultado mismo."
      />
      <div className="max-w-4xl mx-auto px-6 py-12 space-y-12">
        <Notice variant="general" tone="info" />

        <section>
          <div className="flex items-center gap-2 mb-5">
            <Layers className="w-5 h-5 text-cyan-500" />
            <h2 className="font-heading font-black text-2xl text-slate-900">Niveles metodológicos</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {LEVELS.map((l) => (
              <div key={l.n} className="bg-white rounded-lg border border-slate-200 p-6" data-testid={`level-${l.n}`}>
                <span className="inline-flex w-8 h-8 rounded-full bg-slate-900 text-white text-sm font-bold items-center justify-center mb-3">{l.n}</span>
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">Nivel {l.n} — {l.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{l.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-4">No utilizamos "margen de error" ni "nivel de confianza" en consultas abiertas no probabilísticas (niveles 1–3).</p>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-5">
            <CheckCircle2 className="w-5 h-5 text-cyan-500" />
            <h2 className="font-heading font-black text-2xl text-slate-900">Qué medimos (y qué no)</h2>
          </div>
          <p className="text-slate-600 text-sm mb-4">Distinguimos siempre entre estos tipos de información. Nunca presentamos una percepción ciudadana como prueba de una conducta ilegal.</p>
          <ul className="space-y-3">
            {DISTINCTIONS.map(([t, d]) => (
              <li key={t} className="flex gap-3 text-sm">
                <span className="font-semibold text-slate-900 min-w-[170px]">{t}</span>
                <span className="text-slate-600">{d}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-slate-100 rounded-lg p-6 flex gap-4">
          <Bot className="w-6 h-6 text-slate-500 shrink-0" />
          <div>
            <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">Uso de inteligencia artificial</h3>
            <p className="text-slate-600 text-sm leading-relaxed">2Voto puede utilizar herramientas de inteligencia artificial para ayudar a organizar, revisar o resumir información. Las decisiones editoriales y metodológicas importantes son revisadas por personas. La IA nunca publica encuestas, propuestas ni conclusiones de forma automática.</p>
          </div>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-5">
            <ShieldCheck className="w-5 h-5 text-cyan-500" />
            <h2 className="font-heading font-black text-2xl text-slate-900">Confidencialidad y privacidad</h2>
          </div>
          <Notice variant="confidencialidad" />
        </section>

        <section>
          <div className="flex items-center gap-2 mb-5">
            <ScrollText className="w-5 h-5 text-cyan-500" />
            <h2 className="font-heading font-black text-2xl text-slate-900">Avisos</h2>
          </div>
          <div className="space-y-3">
            <Notice variant="resultados" />
            <Notice variant="propuestas" />
            <Notice variant="legal" />
          </div>
        </section>
      </div>
    </div>
  );
}
